import React, { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./context/useAuth";
import { useErrorReporter } from "./context/ErrorContext";
import { LoginPage } from "./components/auth/LoginPage";
import { SignupPage } from "./components/auth/SignupPage";
import { ForgotPasswordPage } from "./components/auth/ForgotPasswordPage";
import { VerifyEmailPage } from "./components/auth/VerifyEmailPage";
import { ResetPasswordPage } from "./components/auth/ResetPasswordPage";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { RouteSkeleton } from "./shared/components/Skeleton";
import AppMaintenancePage from "./pages/AppMaintenancePage";
import SomethingWentWrongPage from "./pages/SomethingWentWrongPage";

// Layout
const AppLayout      = lazy(() => import("./components/layout/AppLayout"));

// App pages — loaded on demand
const LandingPage    = lazy(() => import("./pages/landing/index"));
const ChatPage       = lazy(() => import("./pages/ChatPage"));
const AgentsPage     = lazy(() => import("./pages/AgentsPage"));
const HistoryPage    = lazy(() => import("./pages/HistoryPage"));
const ConfigPage     = lazy(() => import("./pages/ConfigPage"));
const ProvidersPage  = lazy(() => import("./pages/ProvidersPage"));
const ProfilePage    = lazy(() => import("./pages/ProfilePage"));
const NotFoundPage   = lazy(() => import("./pages/NotFoundPage"));

const IS_MAINTENANCE = import.meta.env.VITE_MAINTENANCE_MODE === "true";

/** Wraps a route that requires both authentication AND admin role. */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();
  if (isLoading) return <RouteSkeleton />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  const { isAuthenticated, isLoading } = useAuth();
  const { appError, clearError } = useErrorReporter();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to /chat after any auth method succeeds, even while isLoading is still true
  useEffect(() => {
    if (isAuthenticated && location.pathname === "/login") {
      navigate("/chat", { replace: true });
    }
  }, [isAuthenticated, navigate, location.pathname]);

  /* Maintenance mode — highest priority gate */
  if (IS_MAINTENANCE) return <AppMaintenancePage />;

  /* Backend / API error reported via useErrorReporter() */
  if (appError) {
    return (
      <SomethingWentWrongPage
        message={appError.message}
        error={appError.error}
        onRetry={clearError}
        onHome={() => { clearError(); window.location.href = "/"; }}
      />
    );
  }

  if (isLoading) {
    return <RouteSkeleton />;
  }

  return (
    <Suspense fallback={<RouteSkeleton />}>
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

      {/* Admin-only: provider management */}
      <Route
        path="/providers"
        element={
          <AdminRoute>
            <ProvidersPage />
          </AdminRoute>
        }
      />

      {/* ── App shell: icon-rail layout wraps all authenticated pages ── */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/chat"    element={<ChatPage />} />
        <Route path="/agents"  element={<AgentsPage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/config"  element={<ConfigPage />} />

        {/* Legacy redirects */}
        <Route path="/app"            element={<Navigate to="/chat" replace />} />
        <Route path="/agent-registry" element={<Navigate to="/agents" replace />} />
        <Route path="/settings"       element={<Navigate to="/config" replace />} />
        {/* /canvas removed (2026-08-20) — fully redundant with /chat's own agent-selector + live graph. */}
        <Route path="/canvas"         element={<Navigate to="/chat" replace />} />
      </Route>

      {/* Standalone authenticated pages (no app shell) */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* 404 — must be last */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}

